import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Shield, Loader2, AlertCircle, Check, X as XIcon, UserPlus, Ticket } from 'lucide-react'
import { useAuth } from '../context/AuthProvider'
import { signin, signup, validateInviteCode, getSignupConfig } from '../api/client'

type InviteState =
  | { status: 'idle' }
  | { status: 'checking' }
  | { status: 'valid'; description?: string }
  | { status: 'invalid'; message: string }

export default function SignUp() {
  const navigate = useNavigate()
  const { user, loading: authLoading, refreshMe } = useAuth()

  // Default to requiring an invite until the server responds. Safer than
  // briefly showing an open-signup form that the backend then rejects.
  const { data: signupConfig } = useQuery({
    queryKey: ['signup-config'],
    queryFn: getSignupConfig,
    retry: false,
  })
  const inviteRequired = signupConfig?.inviteRequired ?? true

  const [inviteCode, setInviteCode] = useState('')
  const [invite, setInvite] = useState<InviteState>({ status: 'idle' })
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [username, setUsername] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (authLoading || !user) return
    navigate('/dashboard', { replace: true })
  }, [user, authLoading, navigate])

  useEffect(() => {
    if (!error) return
    const t = setTimeout(() => setError(null), 5000)
    return () => clearTimeout(t)
  }, [error])

  const checkInvite = useCallback(async (code: string) => {
    const trimmed = code.trim()
    if (!trimmed) { setInvite({ status: 'idle' }); return }
    setInvite({ status: 'checking' })
    try {
      const res = await validateInviteCode(trimmed)
      if (res?.valid) setInvite({ status: 'valid', description: res.description })
      else setInvite({ status: 'invalid', message: 'Invite code is not valid' })
    } catch (err) {
      setInvite({ status: 'invalid', message: (err as { message?: string })?.message || 'Invite code is not valid' })
    }
  }, [])

  const onInviteBlur = () => { if (inviteCode.trim()) void checkInvite(inviteCode) }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    if (inviteRequired && invite.status !== 'valid') { setError('Enter a valid invite code.'); return }
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return }
    if (!/[A-Za-z]/.test(password) || !/\d/.test(password)) {
      setError('Password must contain at least one letter and one number.'); return
    }
    if (password !== confirmPassword) { setError('Passwords do not match.'); return }

    setSubmitting(true)
    try {
      const payload: Record<string, string> = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        username: username.trim(),
        email: email.trim(),
        password,
      }
      if (inviteCode.trim()) payload.inviteCode = inviteCode.trim().toUpperCase()
      await signup(payload)
      // Auto-signin with the new credentials.
      await signin({ username: username.trim(), password })
      await refreshMe()
      navigate('/dashboard', { replace: true })
    } catch (err) {
      const e = err as { message?: string }
      setError(e?.message || 'Sign up failed')
      setSubmitting(false)
    }
  }

  if (authLoading || user) {
    return <div className="flex justify-center py-24"><Loader2 size={32} className="animate-spin text-brand-500" /></div>
  }

  return (
    <div className="relative min-h-[calc(100vh-3.5rem)] flex items-center justify-center px-4 py-12">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute left-1/2 -top-24 -translate-x-1/2 h-96 w-[40rem] bg-brand-500/10 blur-3xl rounded-full" />
      </div>

      <div className="relative w-full max-w-md">
        <div className="card p-8 md:p-10 space-y-6">
          <div className="text-center space-y-3">
            <div className="mx-auto w-14 h-14 rounded-2xl bg-gradient-to-br from-brand-500 to-brand-700 ring-4 ring-brand-500/10 flex items-center justify-center shadow-lg">
              <Shield size={28} className="text-white" strokeWidth={2.2} />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-gray-900 dark:text-gray-50">
                Create your account
              </h1>
              <p className="mt-1.5 text-sm text-gray-500 dark:text-gray-400">
                {inviteRequired ? 'IDFY signups are invite-only.' : 'Get started in a minute.'}
              </p>
            </div>
          </div>

          {error && (
            <div role="alert" className="flex items-start gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 text-sm">
              <AlertCircle size={16} className="flex-shrink-0 mt-0.5" aria-hidden="true" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {inviteRequired && (
              <div>
                <label htmlFor="inviteCode" className="label flex items-center gap-1.5">
                  <Ticket size={13} /> Invite code
                </label>
                <div className="relative">
                  <input id="inviteCode" type="text" value={inviteCode}
                    onChange={(e) => { setInviteCode(e.target.value.toUpperCase()); setInvite({ status: 'idle' }) }}
                    onBlur={onInviteBlur}
                    placeholder="IDFY-WELCOME" autoCapitalize="characters"
                    className="input pr-10 font-mono tracking-wider" required />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2">
                    {invite.status === 'checking' && <Loader2 size={14} className="animate-spin text-gray-400" />}
                    {invite.status === 'valid' && <Check size={14} className="text-emerald-500" />}
                    {invite.status === 'invalid' && <XIcon size={14} className="text-red-500" />}
                  </span>
                </div>
                {invite.status === 'valid' && (
                  <p className="mt-1 text-[11px] text-emerald-600 dark:text-emerald-400">
                    {invite.description ? `Valid — ${invite.description}` : 'Invite accepted.'}
                  </p>
                )}
                {invite.status === 'invalid' && (
                  <p className="mt-1 text-[11px] text-red-600 dark:text-red-400">{invite.message}</p>
                )}
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="firstName" className="label">First name</label>
                <input id="firstName" type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)}
                  maxLength={50} className="input" required />
              </div>
              <div>
                <label htmlFor="lastName" className="label">Last name</label>
                <input id="lastName" type="text" value={lastName} onChange={(e) => setLastName(e.target.value)}
                  maxLength={50} className="input" required />
              </div>
            </div>

            <div>
              <label htmlFor="username" className="label">Username</label>
              <input id="username" type="text" autoComplete="username" value={username}
                onChange={(e) => setUsername(e.target.value.trim())}
                pattern="[A-Za-z0-9_.-]{3,20}"
                minLength={3} maxLength={20}
                className="input" required />
              <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">3–20 characters. Letters, digits, _ . -</p>
            </div>

            <div>
              <label htmlFor="email" className="label">Email</label>
              <input id="email" type="email" autoComplete="email" value={email}
                onChange={(e) => setEmail(e.target.value)}
                maxLength={50} className="input" required />
            </div>

            <div>
              <label htmlFor="password" className="label">Password</label>
              <input id="password" type="password" autoComplete="new-password" value={password}
                onChange={(e) => setPassword(e.target.value)}
                minLength={8} maxLength={64}
                className="input" required />
              <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">At least 8 characters, with letters and numbers.</p>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="label">Confirm password</label>
              <input id="confirmPassword" type="password" autoComplete="new-password" value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                minLength={8} maxLength={64}
                className="input" required />
            </div>

            <button type="submit" disabled={submitting || (inviteRequired && invite.status !== 'valid')}
              className="btn-primary w-full justify-center">
              {submitting ? <Loader2 size={16} className="animate-spin" /> : <UserPlus size={16} />}
              Create account
            </button>
          </form>

          <p className="text-center text-sm text-gray-500 dark:text-gray-400">
            Already have an account?{' '}
            <Link to="/signin" className="text-brand-600 hover:text-brand-700 dark:text-brand-400 font-medium">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}
